/**
 * Client-side rate limiter using token bucket algorithm.
 * Uses continuous (fractional) token refill for accuracy.
 * Per-module limiters prevent cross-contamination of rate limits.
 */

interface RateLimiterConfig {
  maxTokens: number;
  tokensPerSecond: number;
}

class RateLimiter {
  private tokens: number;
  private maxTokens: number;
  private refillRatePerMs: number; // tokens per millisecond
  private lastRefill: number;

  constructor({ maxTokens, tokensPerSecond }: RateLimiterConfig) {
    this.maxTokens = maxTokens;
    this.tokens = maxTokens;
    this.refillRatePerMs = tokensPerSecond / 1000;
    this.lastRefill = Date.now();
  }

  private refill() {
    const now = Date.now();
    const elapsed = now - this.lastRefill;
    const tokensToAdd = elapsed * this.refillRatePerMs;
    this.tokens = Math.min(this.maxTokens, this.tokens + tokensToAdd);
    this.lastRefill = now;
  }

  canProceed(): boolean {
    this.refill();
    if (this.tokens >= 1) {
      this.tokens -= 1;
      return true;
    }
    return false;
  }

  getRemainingTokens(): number {
    this.refill();
    return Math.floor(this.tokens);
  }
}

// ── Per-module rate limiters ──────────────────────────────────────────────────

/** Tasks: 20 operations burst, 8/sec sustained */
export const tasksMutationLimiter = new RateLimiter({ maxTokens: 20, tokensPerSecond: 8 });

/** Skills: 20 operations burst, 8/sec sustained */
export const skillsMutationLimiter = new RateLimiter({ maxTokens: 20, tokensPerSecond: 8 });

/** Diary: 15 operations burst, 5/sec sustained */
export const diaryMutationLimiter = new RateLimiter({ maxTokens: 15, tokensPerSecond: 5 });

/** Applications: 15 operations burst, 5/sec sustained */
export const appsMutationLimiter = new RateLimiter({ maxTokens: 15, tokensPerSecond: 5 });

/** Work logs: 15 operations burst, 5/sec sustained */
export const worklogMutationLimiter = new RateLimiter({ maxTokens: 15, tokensPerSecond: 5 });

/** Meetings: 15 operations burst, 5/sec sustained */
export const meetingsMutationLimiter = new RateLimiter({ maxTokens: 15, tokensPerSecond: 5 });

/** Generic mutations (fallback) */
export const mutationLimiter = new RateLimiter({ maxTokens: 30, tokensPerSecond: 10 });

/** Queries */
export const queryLimiter = new RateLimiter({ maxTokens: 50, tokensPerSecond: 20 });

// ── Helper ────────────────────────────────────────────────────────────────────

export function checkRateLimit(limiter: RateLimiter, operationName: string): void {
  if (!limiter.canProceed()) {
    throw new Error(
      `Too many requests for "${operationName}". Please slow down and try again.`
    );
  }
}
